import React, { useState } from "react"

import "./index.scss"
import { ConfigContainer } from "components"
import { Container, Title, Text, Select, Number } from "components/Form"

// Usability configuration component that allows editing of a maintenance record
export default function UsabilityConfig({ maintenanceItem, assetName, onCancel, onSave, onDelete, isCreating = false }) {
  // Initialize the record we're editing with either the provided item or default values
  const [maintenanceRecord, setMaintenanceRecord] = useState(
    maintenanceItem || {
      name: "",
      endDate: new Date().toISOString().split('T')[0], // Default to today
      recurrenceValue: 1,
      recurrenceUnit: "week",
      reminder: 0
    }
  );

  const originalName = maintenanceItem ? maintenanceItem.name : "";

  // Create a collection for the unit options that is compatible with the Select component
  const unitCollection = [
    { id: "day", name: "Days" },
    { id: "week", name: "Weeks" },
    { id: "month", name: "Months" },
    { id: "year", name: "Years" }
  ];

  // Handle changes to the maintenance record fields
  const handleRecordChange = (field, value) => {
    // If the field is recurrenceUnit and value is an array, extract the first element
    if (field === 'recurrenceUnit' && Array.isArray(value) && value.length > 0) {
      value = value[0];
    }

    setMaintenanceRecord(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle form submission
  const handleSubmit = () => {
    if (onSave) {
      onSave(maintenanceRecord);
    }
    return true; // Indicate successful submission to the ConfigContainer
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(maintenanceItem);
    }
  };

  const title = isCreating ?
    `Add new maintenance for ${assetName || 'Instrument'}` :
    `Edit ${originalName} for ${assetName || 'Instrument'}`;

  return (
    <ConfigContainer
      title={title}
      cancelHandler={onCancel}
      submitHandler={handleSubmit}
      deleteHandler={isCreating ? null : handleDelete}
      deleteTitle="Delete"
      confirmDeletionText="Are you sure you want to delete this maintenance?"
    >
      {/* Name field in its own container */}
      <Container className="maintenance-field-container">
        <Title>Name <div className="red-text">*</div></Title>
        <Text
          value={maintenanceRecord.name}
          required
          onChange={(value) => handleRecordChange('name', value)}
          placeholder="Maintenance name"
        />
      </Container>

      {/* End date field in its own container */}
      <Container className="maintenance-field-container">
        <Title>End date <div className="red-text">*</div></Title>
        <Text
          value={maintenanceRecord.endDate}
          onChange={(value) => handleRecordChange('endDate', value)}
          placeholder="YYYY-MM-DD"
          type="date"
        />
      </Container>

      {/* Custom layout for recurrence field to ensure inline display */}
      <div className="custom-recurrence-container">
        <div className="recurrence-label">
          <Title>Recurrence <div className="red-text">*</div></Title>
        </div>
        <div className="recurrence-inputs-wrapper">
          <div className="recurrence-number">
            <Number
              value={maintenanceRecord.recurrenceValue}
              onChange={(value) => handleRecordChange('recurrenceValue', value)}
              min={1}
            />
          </div>
          <div className="recurrence-select">
            <Select
              value={maintenanceRecord.recurrenceUnit}
              onChange={(value) => handleRecordChange('recurrenceUnit', value)}
              collection={unitCollection}
              isMulti={false}
            />
          </div>
        </div>
      </div>

      <Container className="maintenance-field-container">
        <Title>Reminder (days before end date)</Title>
        <Number
          value={maintenanceRecord.reminder}
          onChange={(value) => handleRecordChange('reminder', value)}
          min={0}
          placeholder="Days before expiration to show warning"
        />
      </Container>
    </ConfigContainer>
  );
}
